import type { Dictionary } from './types';

/**
 * Português (Brasil), the original.
 *
 * This file is the reference the other two were written against, and it is the one
 * the unprefixed address bar serves.
 *
 * Four sentences here lost an em-dash that the previous version had, because a dash
 * is a habit borrowed from English prose and it does not survive translation into
 * Spanish. They are split into two sentences or turned into a comma instead, which
 * is the same meaning with one fewer thing to get wrong in the other two languages.
 */
export const ptBR: Dictionary = {
  meta: {
    title: 'Orobas: OrobasHipercube e Orobas Work para macOS',
    description:
      'OrobasHipercube e Orobas Work: dois aplicativos nativos para macOS, escritos em SwiftUI. Um agente de IA super poderoso que age sobre o seu projeto e uma IDE nativa. Em fase beta. Uma conta, uma licença vitalícia. Produto da M2ON.',
    ogTitle: 'Orobas: um agente de IA e uma IDE nativa.',
    ogDescription:
      'O OrobasHipercube é um agente de IA super poderoso que age sobre o seu projeto de verdade. O Orobas Work é a IDE onde o trabalho acontece. SwiftUI nativo, macOS 26, fase beta.',
  },

  chrome: {
    skipToContent: 'Pular para o conteúdo',
    brandName: 'Orobas',
    navMain: 'Principal',
    navApps: 'Aplicativos',
    navPlans: 'Planos',
    navSkins: 'Skins',
    signIn: 'Entrar',
    myAccount: 'Minha conta',
    openMenu: 'Abrir menu',
    closeMenu: 'Fechar menu',
    languageLabel: 'Idioma',
    platformBadge: 'para macOS',
    downloadInstaller: 'Baixar instalador',
  },

  footer: {
    blurb:
      'Dois aplicativos nativos para macOS, escritos em SwiftUI. Sem Electron, sem WebKit, sem telemetria. Produto da M2ON.',
    paymentsLine: 'Pagamentos pelo Mercado Pago.',
    rights: '© {year} {company} - {legalId}.',
    reserved: 'TODOS OS DIREITOS RESERVADOS.',
    companyName: 'M2ON LTDA',
    legalId: 'CNPJ: 68.657.000/0001-00',
    companyLinkLabel: 'M2ON, o site da empresa (abre em uma nova aba)',
  },

  home: {
    heroShortcut: 'abre o {app} de qualquer lugar',
    heroTitleLine1: 'Dois aplicativos nativos.',
    heroTitleLine2: 'Um só agente.',
    heroBody:
      'O *{agent}* é um agente de IA super poderoso que age sobre o seu projeto de verdade. O *{work}* é a IDE nativa onde o trabalho acontece. Em fase beta, foco em desenvolvedores de software, com o objetivo de ser usado em qualquer tipo de trabalho no macOS. Escritos em SwiftUI, produto da M2ON.',
    ctaSignIn: 'Entrar',
    ctaAccount: 'Minha conta',
    ctaMeetApps: 'Conhecer os aplicativos',
    plateAlt: 'Janela compacta do {app}',
    wallpaperAlt:
      'Papel de parede do Orobas: o escudo em traço branco sobre fundo escuro.',
    opensApp: 'abre o {app}',
    ownedBadge: 'Liberado nesta conta',
    notOwnedBadge: 'Ainda não liberado nesta conta',
    togetherTitle: 'Uma conta, uma licença, três lugares',
    togetherBody:
      'Você entra com o mesmo e-mail no site e nos dois aplicativos. A licença é uma linha no banco de dados, não um arquivo no seu computador, então vale nos três lugares ao mesmo tempo. Comprar no site abre os dois aplicativos.',
    togetherColumns: [
      {
        title: 'No site',
        body: 'A conta mostra o que está liberado e onde baixar cada instalador. É onde a compra acontece.',
      },
      {
        title: 'Entre os dois aplicativos',
        body: 'A sessão fica em um arquivo só, e uma mudança nele é anunciada entre os processos. Entrar em um entra no outro, inclusive o logout.',
      },
      {
        title: 'O portão de entrada',
        body: 'Cada aplicativo só abre quando há uma sessão válida e a licença deste aplicativo. A decisão é tomada no banco, nos dois, do mesmo jeito.',
      },
    ],
    requirementsLine: 'Requisitos: {requirements}',
    installerTitle: 'Dois apps, um instalador',
    installerBody:
      'O Instalador Orobas baixa a versão mais recente de cada app, verifica e coloca em /Applications. Um download em vez de dois. Na primeira abertura, clique com o botão direito no app e escolha Abrir.',
    installerDownload: 'Baixar o instalador',
    installerVersion: 'Versão {version}',
  },

  pricing: {
    title: 'Uma licença vitalícia por aplicativo',
    body:
      'Você paga uma vez e usa para sempre, sem assinatura e sem limite de uso. Os aplicativos estão em fase beta. Com Ollama na sua máquina o custo de tokens é zero, e a licença é o que você pagou.',
    loading: 'Carregando planos',
    retry: 'Tentar de novo',
    emptyBody:
      'Nenhum plano está à venda agora. Escreva para {email} e a gente avisa quando abrir.',
    footerNote:
      'Pagamento pelo Mercado Pago. A licença é ativada assim que o pagamento é confirmado.',
    onceLabel: 'pagamento único, vitalício',
    bundleBadge: 'Junto',
    unavailable: 'Indisponível',
    alreadyOwned: 'Já é seu',
    signInToBuy: 'Entrar para comprar',
    buy: 'Comprar',
    openingPayment: 'Abrindo o pagamento…',
    catalogueFailed: 'Não foi possível carregar os planos.',
    checkoutFailed: 'Não foi possível iniciar o pagamento.',
    teaserTitle: 'Uma conta. Os dois aplicativos.',
    teaserBody:
      'Compre uma vez e entre com a mesma conta no site e nos dois aplicativos macOS. A licença é a mesma nos três lugares.',
    teaserCta: 'Ver planos',
  },

  account: {
    crestAlt: 'Escudo de Orobas',
    loadingAccount: 'Carregando sua conta',
    signInTitle: 'Entrar na sua conta',
    signUpTitle: 'Criar sua conta',
    signInBody:
      'A mesma conta vale para o site, para o OrobasHipercube e para o Orobas Work.',
    signUpBody:
      'Uma conta para os dois aplicativos e para o site. A licença é lida dos dois lados.',
    emailLabel: 'E-mail',
    passwordLabel: 'Senha',
    signingIn: 'Entrando…',
    signingUp: 'Criando conta…',
    signInAction: 'Entrar',
    signUpAction: 'Criar conta',
    signInWithGoogle: 'Entrar com o Google',
    signInWithGitHub: 'Entrar com o GitHub',
    orDivider: 'ou',
    noAccountYet: 'Ainda não tem conta?',
    hasAccount: 'Já tem conta?',
    createOne: 'Criar uma',
    confirmTitle: 'Confirme seu e-mail',
    confirmBody:
      'A conta foi criada para *{email}*. Abra o link que chegou na caixa de entrada para confirmá-la. Sem isso o login não funciona.',
    checking: 'Conferindo…',
    confirmedSignIn: 'Já confirmei e quero entrar',
    wrongPassword: 'A senha não confere.',
    backToSignIn: 'Voltar para entrar',
    accountLabel: 'Conta',
    yourAccount: 'Sua conta',
    signOut: 'Sair',
    licensesTitle: 'Suas licenças',
    refreshing: 'Atualizando…',
    refresh: 'Atualizar',
    noLicensesBody:
      'Nenhum aplicativo liberado nesta conta ainda. Assim que um pagamento for confirmado, a licença aparece aqui e nos dois aplicativos ao mesmo tempo.',
    viewPlans: 'Ver planos',
    lifetimeLicense: 'Licença vitalícia',
    licensedFallback: 'Licenciado',
    validUntil: 'até {date}',
    installersTitle: 'Instaladores',
    download: 'Baixar',
    versionLabel: 'Versão {version}',
    gatekeeperNote:
      'Os dois pacotes são assinados ad-hoc e ainda não passaram pelo notarization da Apple. Na primeira vez, clique com o botão direito no app dentro do zip e escolha Abrir. O Gatekeeper avisa uma única vez.',
    oauthFailedTitle: 'Não foi possível completar o login',
    oauthFailedBody: 'Tente novamente ou entre com o e-mail e a senha.',
    order: {
      readFailedTitle: 'Não deu para ler o pedido',
      signInRequiredTitle: 'Entre para ver este pedido',
      signInRequiredBody:
        'Esta página mostra o resultado de um pedido para a conta que fez a compra.',
      checkingTitle: 'Conferindo seu pagamento…',
      checkingBody: 'Isso costuma levar alguns segundos.',
      approvedTitle: 'Pagamento confirmado',
      approvedBody:
        'Sua licença está valendo. Ela já vale para o OrobasHipercube, para o Orobas Work e para esta conta.',
      incompleteTitle: 'O pagamento não foi concluído',
      incompleteBody: 'Nada foi cobrado e nenhuma licença foi ativada.',
      expiredTitle: 'Este pedido expirou',
      expiredBody: 'A preferência de pagamento venceu antes da confirmação.',
      pendingTitle: 'Aguardando confirmação',
      pendingBody:
        'O Mercado Pago ainda está confirmando o pagamento. Esta página atualiza sozinha.',
      goToAccount: 'Ir para minha conta',
    },
  },

  skinNFT: {
    title: 'Skins do OrobasHipercube',
    subtitle: 'Personalize seu agente com acessórios exclusivos',
    owned: 'Skins que você possui',
    available: 'Skins disponíveis',
    equip: 'Equipar',
    unequip: 'Desequipar',
    purchase: 'Comprar',
    loading: 'Carregando skins…',
    emptyOwned: 'Você ainda não possui nenhuma skin. Compre na loja para personalizar seu OrobasHipercube!',
    emptyAvailable: 'Nenhuma skin disponível no momento.',
    slotHat: 'Chapéu',
    slotGlasses: 'Óculos',
    slotEye: 'Olhos',
    slotNose: 'Nariz',
    slotMouth: 'Boca',
    slotBody: 'Corpo',
    rarityCommon: 'Comum',
    rarityRare: 'Raro',
    rarityEpic: 'Épico',
    rarityLegendary: 'Lendário',
    equippedBadge: 'Equipado',
    syncMessage: 'Suas skins são sincronizadas automaticamente com o app OrobasHipercube no macOS.',
    previewTitle: 'Pré-visualização',
    animationNote: 'Acessórios para olhos, nariz e boca seguem a animação do seu agente.',
  },

  callback: {
    crestAlt: 'Escudo de Orobas',
    openingTitle: 'Abrindo o {app}…',
    openingBody: 'Só um instante.',
    blockedTitle: '{app} não abriu automaticamente',
    blockedBody:
      'Alguns navegadores bloqueiam a passagem para aplicativos. Toque abaixo para abrir o {app} e terminar o login.',
    openAppCta: 'Abrir o {app}',
    declinedTitle: 'Login cancelado',
    declinedCancelled: 'Você cancelou o login com o Google.',
    declinedGeneric: 'O Google recusou o login.',
    unknownTitle: 'Este link não é de um aplicativo',
    unknownBody:
      'O endereço que abriu esta página não nomeia nenhum aplicativo do Orobas, então não há para onde devolver o login.',
    incompleteTitle: 'Login incompleto',
    incompleteBody:
      'Faltou {missing} nesta volta. Abra o aplicativo e comece o login de novo, ele leva alguns segundos e leva você até aqui outra vez.',
    signInHere: 'Entrar pelo site',
  },

  api: {
    offline: 'Sem conexão com o servidor. Verifique a internet e tente de novo.',
    unexpected: 'Algo deu errado. Tente de novo.',
    signInFailed: 'Não foi possível entrar. Tente de novo.',
    sessionExpired: 'Sessão expirada. Entre novamente.',
    alreadyRegistered: 'Este e-mail já tem conta. Entre com a sua senha.',
    accountOffline: 'Sem conexão com o servidor de contas.',
    badCredentials: 'E-mail ou senha incorretos.',
    emailNotConfirmed:
      'Confirme seu e-mail antes de entrar. O link chegou na sua caixa de entrada.',
    passwordTooShort: 'A senha precisa ter pelo menos 6 caracteres.',
    invalidEmail: 'Esse e-mail não parece válido.',
    rateLimited: 'Muitas tentativas. Aguarde um instante e tente de novo.',
  },
};